import { locatesWorkerSchemas } from './schemas';
import type { Instant } from '@js-joda/core';
import type { LocateOrder } from '@thinkalpha/ui-types/contracts/locates/orders';
import type { LocateProvider } from '@thinkalpha/ui-types/contracts/locates/providers';
import type { FlattenedLocateResultRow } from '@thinkalpha/ui-types/types/labModern';
import type { LocatesV2Filters } from 'src/models/LocatesV2WidgetModel/filters';
import type { LocatesV2Sort } from 'src/queries/locatesV2/sorts';
import { defineWorkerManifest } from 'src/services/workerHost/contract';
export type LocatesWorkerRequest =
    | { type: 'updateProviders'; providers: LocateProvider[] }
    | { type: 'initialFetch'; limit?: number }
    | { type: 'upsertOrders'; orders: LocateOrder[] }
    | { type: 'socketUpdate'; order: LocateOrder }
    | {
          type: 'query';

          direction: 'initial' | 'up' | 'down';
          limit: number;
          anchorOffset?: number;
          anchorStableOrderId?: string; // Required for 'up' and 'down'
          fallbackAnchorOffset?: number;
          fallbackAnchorStableOrderIds?: string[];
          includeAnchor?: boolean;
      }
    | { type: 'updateSort'; sort: LocatesV2Sort }
    | { type: 'updateFilters'; filters: LocatesV2Filters }
    | {
          type: 'socketUpdateWithRequery';

          order: LocateOrder;
          anchorStableOrderId: string | null;
          limit: number;
      }
    | {
          type: 'initializeSocket';

          connectionSettings: { uri: string };
          accountIds: string[];
          instanceId: string;
      }
    | {
          type: 'exportCsv';

          columnOrder: string[];
          columnVisibility: Record<string, boolean>;
      };

export interface LocatesActivity {
    id: string;
    accountId: string;
    symbol: string;
    resultState: 'accepted' | 'rejected';
    timestamp: Instant;
    orderQuantity: number;
    price: number | null;
    providerId: string;
    rejectReason: string | null;
    activityUserId: string | null;
}

export type LocatesWorkerResponse =
    | { type: 'updateProviders'; success: boolean }
    | { type: 'initialFetchComplete'; orderCount: number }
    | { type: 'ordersUpserted'; success: boolean }
    | { type: 'socketUpdateComplete' }
    | {
          type: 'queryResult';

          rows: FlattenedLocateResultRow[];
          isStartOfData: boolean;
          isEndOfData: boolean;
      }
    | { type: 'sortUpdateComplete' }
    | { type: 'filtersUpdateComplete' }
    | {
          type: 'socketUpdateWithRequeryResult';

          rows: FlattenedLocateResultRow[];
          isEndOfDataUpward: boolean;
          isEndOfDataDownward: boolean;
      }
    | { type: 'filterCacheInvalidated' }
    | { type: 'socketInitialized'; success: boolean }
    | {
          type: 'socketConnectionStatus';

          status: 'uninitialized' | 'connecting' | 'connected' | 'disconnected';
      }
    | { type: 'locatesActivity'; activity: LocatesActivity }
    | { type: 'exportCsvResult'; success: true; csvData: string; rowCount: number }
    | { type: 'exportCsvResult'; success: false; error: string };

type ResponseTypes = {
    updateProviders: 'updateProviders';
    initialFetch: 'initialFetchComplete';
    upsertOrders: 'ordersUpserted';
    socketUpdate: 'socketUpdateComplete';
    query: 'queryResult';
    updateSort: 'sortUpdateComplete';
    updateFilters: 'filtersUpdateComplete';
    socketUpdateWithRequery: 'socketUpdateWithRequeryResult';
    initializeSocket: 'socketInitialized';
    exportCsv: 'exportCsvResult';
};
export interface LocatesWorkerContract {
    requests: {
        [K in LocatesWorkerRequest['type']]: {
            request: Omit<Extract<LocatesWorkerRequest, { type: K }>, 'type'>;
            response: Extract<LocatesWorkerResponse, { type: ResponseTypes[K] }>;
        };
    };
    events: {
        rowCountsChanged: { filteredCount: number; totalCount: number };
        filterCacheInvalidated: Record<string, never>;
        socketConnectionStatus: { status: 'uninitialized' | 'connecting' | 'connected' | 'disconnected' };
        locatesActivity: { activity: LocatesActivity };
        ordersReset: Record<string, never>;
    };
}
export const locatesWorkerManifest = defineWorkerManifest<LocatesWorkerContract>({
    state: ['session', 'featureFlags', 'user'],
    codec: 'zod',
    schemas: locatesWorkerSchemas,
});
